/**
 * DISCOUNT_PLUMBERS_LandingPage_v01_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v01
 * Date: 2025-08-30 (America/Denver)
 *
 * Built from EASY_RENTERS_LandingPage_v02O_WIZARD:
 * - Same wizard/vertical toggle, progress, blur-based validation, Enter-trap.
 * - Same modular payload pipeline: buildLeadPayload -> toWirePayloadStrings -> postLead.
 * - Sectioned confirmation (no JSON), works in Wizard (Step 4) + Vertical modes.
 *
 * Plumber-specific updates:
 * - Business section: companyName, businessType (LLC/Sole/S-Corp/C-Corp/Partnership/Other), yearsInBusiness, website (optional).
 * - Operations section: workType (Residential/Commercial/Both), services (Service/Repair, New Construction, Sewer/Drain, Gas Lines, Water Heaters, Excavation/Trenching), employees/owners, subcontractors usage.
 * - Coverage section: GL limit (1M/2M, 2M/4M, custom), Workers’ Comp (Y/N), Commercial Auto (Y/N), Bonding (Y/N), Tools & Equipment amount, # Commercial Vehicles.
 * - Risk & History: prior claims (5y), license # (optional), annual revenue & payroll (optional), torch/soldering (Y/N), notes.
 */

import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
  Carousel,
  Accordion,
} from "react-bootstrap";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v01";
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const imagePath = "insurance/images/PLUMBERS-Insurance/";

/* ============================================================
   TYPES
============================================================ */
type BusinessType = "" | "sole" | "llc" | "s_corp" | "c_corp" | "partnership" | "other";
type WorkType = "" | "residential" | "commercial" | "both";
type GLLimit = "" | "1M/2M" | "2M/4M" | "custom";

/** Raw lead payload (typed for app state) */
interface ILeadPayload {
  // Contact
  fullName: string;
  email: string;
  phone: string;

  // Business identity
  companyName: string;
  businessType: BusinessType;
  yearsInBusiness: number;
  website?: string;

  // Location
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;

  // Operations
  workType: WorkType;
  doesServiceRepair: boolean;
  doesNewConstruction: boolean;
  doesSewerDrain: boolean;
  doesGasLines: boolean;
  doesWaterHeaters: boolean;
  doesExcavation: boolean;

  ownersCount: number;
  employeesCount: number;
  usesSubcontractors: boolean;
  subcontractorPercent?: string; // numeric string

  // Coverage
  glLimit: GLLimit;
  glLimitCustom?: string; // numeric string if custom
  wantsWorkersComp: boolean;
  wantsCommercialAuto: boolean;
  wantsBonding: boolean;
  toolsEquipmentCoverage: string; // numeric string
  numCommercialVehicles?: number;

  // Risk & history
  priorClaimsCount5y: number;
  licenseNumber?: string;
  annualRevenue?: string; // numeric string
  annualPayroll?: string;  // numeric string
  doesTorchSoldering: boolean;

  notes?: string;

  // meta
  utm?: { source?: string; medium?: string; campaign?: string };
  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;
}

/** Wire payload where EVERY VALUE IS A STRING */
type WirePayload = Record<string, string>;

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

function digitsOnly(s: string): string {
  return (s ?? "").replace(/\D+/g, "");
}
function isValidEmail(email: string): boolean {
  return /.+@.+\..+/.test(email);
}
function isValidPhone(raw: string): boolean {
  return digitsOnly(raw).length === 10;
}
function isValidZipCode(raw: string): boolean {
  return digitsOnly(raw).length === 5;
}
function formatPhonePretty(raw: string): string {
  const d = digitsOnly(raw);
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
function cap(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
function money(nLike: string) {
  const n = Number(digitsOnly(nLike));
  if (!Number.isFinite(n)) return nLike;
  try {
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  } catch {
    return `$${n}`;
  }
}
const dash = (v?: string) => (v && v.trim().length ? v : "—");

/* ============================================================
   SLIDES
============================================================ */
interface Slide {
  src: string;
  alt: string;
  title: string;
  subline: string;
  smallCaption: string;
}
const slides: Slide[] = [
  {
    src: imagePath + "AdobeStock_1099111111_Preview.jpg",
    alt: "Plumber working under a sink with tools.",
    title: "Protect Your Plumbing Business",
    subline: "Get tailored coverage for jobs big and small—fast.",
    smallCaption: "GL, Workers’ Comp, Auto, Tools.",
  },
  {
    src: imagePath + "AdobeStock_1222333444_Preview.jpg",
    alt: "Copper pipes and torch setup.",
    title: "Hot Work? Gas Lines? New Builds?",
    subline: "Tell us what you do—we’ll match the right carriers.",
    smallCaption: "Honest guidance. Clear options.",
  },
  {
    src: imagePath + "AdobeStock_1333555666_Preview.jpg",
    alt: "Service van stocked with plumbing parts.",
    title: "Cover Your Vans & Your Gear",
    subline: "Commercial auto and equipment coverage—made simple.",
    smallCaption: "Save time. Save money.",
  },
];

/* ============================================================
   PLUMBER FAQ (inline) + simple Accordion renderer
============================================================ */
// type FAQ = { id: string; question: string; answer: string };

interface IFAQ{
  id:string;
  question:string;
  answer:string;
}


const plumberFAQs: IFAQ[] = 
[
  { id: "q1", question: "What insurance does a plumbing business need?", answer: "Most plumbers carry General Liability (GL), Workers’ Comp (when you have employees), Commercial Auto for vans/trucks, and Tools & Equipment coverage. Bonds may be required for certain permits or contracts." },
  { id: "q2", question: "How much GL coverage should I choose?", answer: "1M/2M is common for small contractors, while larger commercial jobs may require 2M/4M or project-specific limits." },
  { id: "q3", question: "Do I need Workers’ Comp if I use subs?", answer: "If you hire subcontractors, carriers may ask for their COIs. If they’re uninsured or deemed employees, you may need Workers’ Comp." },
  { id: "q4", question: "Does torch soldering affect my premium?", answer: "Hot work can impact pricing and carrier eligibility. We’ll help you place it correctly." },
  { id: "q5", question: "Can I schedule tools and equipment?", answer: "Yes. Hand tools and larger equipment (jetters, trenchers) can be covered—often with off-premises theft protection." },
  { id: "q6", question: "How fast can I get a COI?", answer: "Often same-day once bound. We can add Additional Insureds and endorsements per job requirements." },
  { id: "q7", question: "What if I’ve had prior claims?", answer: "Still fine—just tell us what happened and what changed. We’ll shop carriers to find the best fit." },
  { id: "q8", question: "Do you cover new construction?", answer: "Yes—let us know your work mix (service vs. new builds) and we’ll tailor coverage." },
  { id: "q9", question: "Does insurance cover water damage I cause?", answer: "Yes, accidental water damage from your work is typically covered under General Liability." },
  { id: "q10", question: "What about sewer line or drain work?", answer: "Drain cleaning, sewer repair, and excavation can be included—just disclose your scope for proper rating." },
  { id: "q11", question: "Do I need bonding?", answer: "Many municipalities and commercial jobs require license or permit bonds. We can place those alongside your insurance." },
  { id: "q12", question: "Are apprentice plumbers covered?", answer: "Yes, but they should be listed on payroll for Workers’ Comp and factored into liability exposure." },
  { id: "q13", question: "Does Commercial Auto cover my ladder racks and signage?", answer: "The vehicle is covered, but attached racks and customizations should be scheduled if valuable." },
  { id: "q14", question: "Can I cover rented equipment?", answer: "Yes, rental reimbursement and coverage for leased equipment are available under inland marine policies." },
  { id: "q15", question: "Will my personal auto policy cover plumbing work?", answer: "No—personal auto excludes business use. You need Commercial Auto for work vehicles." },
  { id: "q16", question: "What’s the difference between GL and Professional Liability?", answer: "GL covers bodily injury/property damage. Professional Liability (E&O) covers mistakes in design, consulting, or advice." },
  { id: "q17", question: "Are after-hours or emergency calls covered?", answer: "Yes, but higher risk work (midnight gas leaks, sewer backups) should be disclosed for accurate pricing." },
  { id: "q18", question: "Do you cover multi-state operations?", answer: "Yes—let us know where you operate. Some states require separate filings for Workers’ Comp." },
  { id: "q19", question: "Can I pay monthly?", answer: "Most policies offer pay-in-full or monthly billing with a small financing fee." },
  { id: "q20", question: "Do carriers check my license?", answer: "Yes, many carriers verify active state plumbing licenses before binding coverage." }
]



// const plumberFAQs: FAQ[] = [
//   { id: "q1", question: "What insurance does a plumbing business need?", answer: "Most plumbers carry General Liability (GL), Workers’ Comp (when you have employees), Commercial Auto for vans/trucks, and Tools & Equipment coverage. Bonds may be required for certain permits or contracts." },
//   { id: "q2", question: "How much GL coverage should I choose?", answer: "1M/2M is common for small contractors, while larger commercial jobs may require 2M/4M or project-specific limits." },
//   { id: "q3", question: "Do I need Workers’ Comp if I use subs?", answer: "If you hire subcontractors, carriers may ask for their COIs. If they’re uninsured or deemed employees, you may need Workers’ Comp." },
//   { id: "q4", question: "Does torch soldering affect my premium?", answer: "Hot work can impact pricing and carrier eligibility. We’ll help you place it correctly." },
//   { id: "q5", question: "Can I schedule tools and equipment?", answer: "Yes. Hand tools and larger equipment (jetters, trenchers) can be covered—often with off-premises theft protection." },
//   { id: "q6", question: "How fast can I get a COI?", answer: "Often same-day once bound. We can add Additional Insureds and endorsements per job requirements." },
//   { id: "q7", question: "What if I’ve had prior claims?", answer: "Still fine—just tell us what happened and what changed. We’ll shop carriers to find the best fit." },
//   { id: "q8", question: "Do you cover new construction?", answer: "Yes—let us know your work mix (service vs. new builds) and we’ll tailor coverage." },
// ];

const FAQ_v01_Simple: React.FC<{ faqs: IFAQ[]; openFirst?: boolean }> = ({ faqs, openFirst }) => (
  <Accordion defaultActiveKey={openFirst && faqs.length ? faqs[0].id : undefined} alwaysOpen>
    {faqs.map((f) => (
      <Accordion.Item eventKey={f.id} key={f.id}>
        <Accordion.Header>{f.question}</Accordion.Header>
        <Accordion.Body>{f.answer}</Accordion.Body>
      </Accordion.Item>
    ))}
  </Accordion>
);

/* ============================================================
   MAIN
============================================================ */
export const DISCOUNT_PLUMBERS_LandingPage_v02O_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Contact
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Business identity
  const [companyName, setCompanyName] = useState("");
  const [businessType, setBusinessType] = useState<BusinessType>("");
  const [yearsInBusiness, setYearsInBusiness] = useState<number>(0);
  const [website, setWebsite] = useState<string>("");

  // Location
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Operations
  const [workType, setWorkType] = useState<WorkType>("");
  const [doesServiceRepair, setDoesServiceRepair] = useState(true);
  const [doesNewConstruction, setDoesNewConstruction] = useState(false);
  const [doesSewerDrain, setDoesSewerDrain] = useState(false);
  const [doesGasLines, setDoesGasLines] = useState(false);
  const [doesWaterHeaters, setDoesWaterHeaters] = useState(true);
  const [doesExcavation, setDoesExcavation] = useState(false);

  const [ownersCount, setOwnersCount] = useState<number>(1);
  const [employeesCount, setEmployeesCount] = useState<number>(0);
  const [usesSubcontractors, setUsesSubcontractors] = useState<boolean>(false);
  const [subcontractorPercent, setSubcontractorPercent] = useState<string>("");

  // Coverage
  const [glLimit, setGlLimit] = useState<GLLimit>("1M/2M");
  const [glLimitCustom, setGlLimitCustom] = useState<string>("");
  const [wantsWorkersComp, setWantsWorkersComp] = useState<boolean>(false);
  const [wantsCommercialAuto, setWantsCommercialAuto] = useState<boolean>(true);
  const [wantsBonding, setWantsBonding] = useState<boolean>(false);
  const [toolsEquipmentCoverage, setToolsEquipmentCoverage] = useState<string>("10000");
  const [numCommercialVehicles, setNumCommercialVehicles] = useState<number>(1);

  // Risk & history
  const [priorClaimsCount5y, setPriorClaimsCount5y] = useState<number>(0);
  const [licenseNumber, setLicenseNumber] = useState<string>("");
  const [annualRevenue, setAnnualRevenue] = useState<string>("");
  const [annualPayroll, setAnnualPayroll] = useState<string>("");
  const [doesTorchSoldering, setDoesTorchSoldering] = useState<boolean>(true);

  // Misc
  const [notes, setNotes] = useState<string>("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);

  // Keep the exact data sent (for confirmation display)
  const [lastSubmittedApp, setLastSubmittedApp] = useState<ILeadPayload | null>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ["Contact & Business", "Operations & Coverage", "Risk & Notes", "Confirmation"] as const;

  /* ---------- Touched + validation helpers (blur-driven) ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });

  // Optional-field validation props: green when filled & valid; neutral when blank; never red on blank.
  const vpOpt = (name: string, valuePresent: boolean, passIfPresent: boolean = true) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && valuePresent && passIfPresent,
    isInvalid: isTouched(name) && valuePresent && !passIfPresent,
    className: isTouched(name) && valuePresent && passIfPresent ? "bg-success-subtle" : undefined,
  });

  // Validators (blank optional fields pass)
  const V = {
    // Contact
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: isValidPhone(phone),

    // Business identity
    companyName: companyName.trim().length >= 2,
    businessType: businessType !== "",
    yearsInBusiness: Number.isFinite(yearsInBusiness) && yearsInBusiness >= 0 && yearsInBusiness <= 80,

    // Location
    address1: address1.trim().length >= 3,
    city: city.trim().length >= 2,
    state: state !== "",
    zip: isValidZipCode(zipCode),

    // Operations
    workType: workType !== "",
    ownersCount: Number.isFinite(ownersCount) && ownersCount >= 1 && ownersCount <= 10,
    employeesCount: Number.isFinite(employeesCount) && employeesCount >= 0 && employeesCount <= 200,
    subcontractorPercent:
      !usesSubcontractors || (digitsOnly(subcontractorPercent) !== "" && Number(digitsOnly(subcontractorPercent)) <= 100),

    // Coverage
    glLimit: glLimit !== "",
    glLimitCustom: glLimit !== "custom" || (digitsOnly(glLimitCustom) !== "" && Number(digitsOnly(glLimitCustom)) >= 1000000),
    toolsEquipmentCoverage: digitsOnly(toolsEquipmentCoverage) !== "" && Number(digitsOnly(toolsEquipmentCoverage)) >= 0,
    numCommercialVehicles: !wantsCommercialAuto || (Number.isFinite(numCommercialVehicles) && numCommercialVehicles >= 0 && numCommercialVehicles <= 50),

    // Risk & history
    priorClaimsCount5y: Number.isFinite(priorClaimsCount5y) && priorClaimsCount5y >= 0 && priorClaimsCount5y <= 20,

    // Optional fields
    website: !website || website.trim().length >= 4,
    licenseNumber: !licenseNumber || licenseNumber.trim().length >= 3,
    annualRevenue: !annualRevenue || digitsOnly(annualRevenue) !== "",
    annualPayroll: !annualPayroll || digitsOnly(annualPayroll) !== "",
    notes: !notes || notes.trim().length >= 2,
  };

  const valid = useMemo(() => {
    return (
      V.fullName &&
      V.email &&
      V.phone &&
      V.companyName &&
      V.businessType &&
      V.yearsInBusiness &&
      V.address1 &&
      V.city &&
      V.state &&
      V.zip &&
      V.workType &&
      V.ownersCount &&
      V.employeesCount &&
      V.subcontractorPercent &&
      V.glLimit &&
      V.glLimitCustom &&
      V.toolsEquipmentCoverage &&
      V.numCommercialVehicles &&
      V.priorClaimsCount5y &&
      V.website &&
      V.licenseNumber &&
      V.annualRevenue &&
      V.annualPayroll &&
      V.notes
    );
  }, [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return (
          V.fullName &&
          V.email &&
          V.phone &&
          V.companyName &&
          V.businessType &&
          V.yearsInBusiness &&
          V.address1 &&
          V.city &&
          V.state &&
          V.zip
        );
      case 1:
        return (
          V.workType &&
          V.ownersCount &&
          V.employeesCount &&
          V.subcontractorPercent &&
          V.glLimit &&
          V.glLimitCustom &&
          V.toolsEquipmentCoverage &&
          V.numCommercialVehicles
        );
      case 2:
        return V.priorClaimsCount5y && V.website && V.licenseNumber && V.annualRevenue && V.annualPayroll && V.notes;
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) =>
      setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));
    if (currentStep === 0)
      touchMany(["fullName", "email", "phone", "companyName", "businessType", "yearsInBusiness", "address1", "city", "state", "zip"]);
    else if (currentStep === 1)
      touchMany([
        "workType",
        "ownersCount",
        "employeesCount",
        "subcontractorPercent",
        "glLimit",
        "glLimitCustom",
        "toolsEquipmentCoverage",
        "numCommercialVehicles",
      ]);
    else if (currentStep === 2)
      touchMany(["priorClaimsCount5y", "website", "licenseNumber", "annualRevenue", "annualPayroll", "notes"]);
    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ============================================================
     PAYLOAD & SUBMIT (MODULARIZED)
  ============================================================ */

  function buildLeadPayload(): ILeadPayload {
    return {
      // Contact
      fullName: fullName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),

      // Business identity
      companyName: companyName.trim(),
      businessType,
      yearsInBusiness: Number(yearsInBusiness),
      website: website?.trim() || "",

      // Location
      address1: address1.trim(),
      address2: address2?.trim() || "",
      city: city.trim(),
      state: String(state),
      zipCode: String(zipCode),

      // Operations
      workType,
      doesServiceRepair,
      doesNewConstruction,
      doesSewerDrain,
      doesGasLines,
      doesWaterHeaters,
      doesExcavation,

      ownersCount: Number(ownersCount),
      employeesCount: Number(employeesCount),
      usesSubcontractors,
      subcontractorPercent: usesSubcontractors ? String(Number(digitsOnly(subcontractorPercent))) : "0",

      // Coverage
      glLimit,
      glLimitCustom: glLimit === "custom" ? String(Number(digitsOnly(glLimitCustom))) : "",
      wantsWorkersComp,
      wantsCommercialAuto,
      wantsBonding,
      toolsEquipmentCoverage: String(Number(digitsOnly(toolsEquipmentCoverage))),
      numCommercialVehicles: wantsCommercialAuto ? Number(numCommercialVehicles) : 0,

      // Risk & history
      priorClaimsCount5y: Number(priorClaimsCount5y),
      licenseNumber: licenseNumber?.trim() || "",
      annualRevenue: annualRevenue ? String(Number(digitsOnly(annualRevenue))) : "",
      annualPayroll: annualPayroll ? String(Number(digitsOnly(annualPayroll))) : "",
      doesTorchSoldering,

      // Misc
      notes: notes?.trim() || "",

      // meta
      domainName: typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.plumbers.quote",
      itemClass: "quote.request",
      itemType: "legacy.plumbers.landing",
      clientVersion: String(VERSION),
    };
  }

  function toWirePayloadStrings(p: ILeadPayload): WirePayload {
    const out: WirePayload = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string") {
        out[k] = v;
      } else if (typeof v === "number") {
        out[k] = Number.isFinite(v) ? `${v}` : "";
      } else if (typeof v === "boolean") {
        out[k] = v ? "true" : "false";
      } else if (v && typeof v === "object") {
        try {
          out[k] = JSON.stringify(v);
        } catch {
          out[k] = "";
        }
      } else {
        out[k] = "";
      }
    }
    return out;
  }

  async function postLead(payload: WirePayload): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "cors",
    });

    let resJson: unknown = null;
    try {
      const txt = await res.text();
      resJson = txt ? JSON.parse(txt) : null;
    } catch {
      resJson = null;
    }

    const asObj = (x: unknown): Record<string, unknown> | null =>
      x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : null;

    if (!res.ok) {
      const o = asObj(resJson);
      const msg = (o?.error as string) || (o?.message as string) || `Request failed: ${res.status}`;
      return { ok: false, status: res.status, message: msg };
    }

    const o = asObj(resJson);
    const id = (o?.id as string) || (o?.requestId as string) || undefined;
    return { ok: true, status: res.status, id };
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    // In wizard mode, ignore submit on steps 0–1 and just advance
    if (isWizard && currentStep < 2) {
      goNext();
      return;
    }

    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    // Touch all relevant fields so feedback appears if invalid
    setTouched((t) => ({
      ...t,
      fullName: true, email: true, phone: true,
      companyName: true, businessType: true, yearsInBusiness: true,
      address1: true, city: true, state: true, zip: true,
      workType: true, ownersCount: true, employeesCount: true, subcontractorPercent: true,
      glLimit: true, glLimitCustom: true, toolsEquipmentCoverage: true, numCommercialVehicles: true,
      priorClaimsCount5y: true, website: true, licenseNumber: true, annualRevenue: true, annualPayroll: true, notes: true,
    }));

    if (!valid) {
      setSubmitError("Please complete all required fields.");
      return;
    }

    // 1) Build typed app payload
    const appPayload = buildLeadPayload();

    // 2) Convert to string-only wire payload
    const wirePayload = toWirePayloadStrings(appPayload);

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload (wire):", wirePayload);
        await new Promise((r) => setTimeout(r, 600));
        const status = { ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) };
        setServerStatus(status);
        setLastSubmittedApp(appPayload);
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      // 3) Post
      const status = await postLead(wirePayload);

      // 4) Update UI
      setServerStatus(status);
      if (!status.ok) throw new Error(status.message || "Unknown server error");

      // Persist what was sent so the confirmation reflects the exact data
      setLastSubmittedApp(appPayload);

      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Safe active slide
  const active = slides[Math.min(slides.length - 1, Math.max(0, Number.isFinite(activeSlide) ? activeSlide : 0))];

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Discount <span className="text-primary">Plumbers</span> Insurance
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Speak with an agent:</div>
              <Button variant="outline-primary" href="tel:3033374433">{AGENT_PHONE_NUMBER}</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO with background Carousel */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "760px" }}>
        <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(typeof i === "number" ? i : 0)}
            controls
            indicators
            fade
            interval={3500}
            className="w-100 h-100"
          >
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 760,
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-start g-4 p-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">GL • WC • Auto • Tools</Badge>
                  <Badge bg="light" text="dark">Additional Insureds</Badge>
                  <Badge bg="light" text="dark">Licensed Advisors</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" onClick={scrollToForm}>Get My Quote</Button>
                  <Button variant="outline-light" href="#faq">Common Questions</Button>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Fast, No-Obligation Quote</div>
                      <small className="text-body-secondary">Tell us about your plumbing business to see options.</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      Server: {submitError}
                    </Alert>
                  )}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          if (submitOk && e.currentTarget.checked) setCurrentStep(3);
                          else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields in one view.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  {/* FORM START */}
                  <Form
                    onSubmit={handleSubmit}
                    noValidate
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isWizard) {
                        if (currentStep < 2) {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isStepValid(currentStep)) goNext();
                        }
                      }
                    }}
                  >
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {/* STEP 1: CONTACT & BUSINESS */}
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Contact &amp; Business</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={6}>
                                    <Form.Label htmlFor="fullName">Full Name</Form.Label>
                                    <Form.Control
                                      id="fullName"
                                      value={fullName}
                                      onChange={(e) => setFullName(e.target.value)}
                                      placeholder="Jane Doe"
                                      required
                                      {...vp("fullName", V.fullName)}
                                    />
                                    <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="companyName">Company Name</Form.Label>
                                    <Form.Control
                                      id="companyName"
                                      value={companyName}
                                      onChange={(e) => setCompanyName(e.target.value)}
                                      placeholder="Acme Plumbing LLC"
                                      required
                                      {...vp("companyName", V.companyName)}
                                    />
                                    <Form.Control.Feedback type="invalid">Please enter your company name.</Form.Control.Feedback>
                                  </Col>



                                  <Col md={4}>
                                    <Form.Label htmlFor="website">Website (optional)</Form.Label>
                                    <Form.Control
                                      id="website"
                                      value={website}
                                      onChange={(e) => setWebsite(e.target.value)}
                                      placeholder="https://..."
                                      {...vpOpt("website", !!website, V.website)}
                                    />
                                  </Col>


                                  <Col md={4}>
                                    <Form.Label htmlFor="email">Email</Form.Label>
                                    <Form.Control
                                      id="email"
                                      type="email"
                                      value={email}
                                      onChange={(e) => setEmail(e.target.value)}
                                      placeholder="you@example.com"
                                      required
                                      {...vp("email", V.email)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="phone">Phone</Form.Label>
                                    <Form.Control
                                      id="phone"
                                      inputMode="tel"
                                      value={phone}
                                      onChange={(e) => setPhone(e.target.value)}
                                      placeholder="(555) 123-4567"
                                      required
                                      {...vp("phone", V.phone)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={8}>
                                    <Form.Label htmlFor="address1">Business Address</Form.Label>
                                    <Form.Control
                                      id="address1"
                                      value={address1}
                                      onChange={(e) => setAddress1(e.target.value)}
                                      placeholder="123 Main St"
                                      required
                                      {...vp("address1", V.address1)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter your street address.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="address2">Unit / Suite</Form.Label>
                                    <Form.Control
                                      id="address2"
                                      value={address2}
                                      onChange={(e) => setAddress2(e.target.value)}
                                      placeholder="Suite 200"
                                      {...vpOpt("address2", !!address2.trim())}
                                    />
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="city">City</Form.Label>
                                    <Form.Control
                                      id="city"
                                      value={city}
                                      onChange={(e) => setCity(e.target.value)}
                                      placeholder="City"
                                      required
                                      {...vp("city", V.city)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter your city.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="state">State</Form.Label>
                                    <Form.Select
                                      id="state"
                                      value={state}
                                      onChange={(e) => setState(e.target.value)}
                                      required
                                      {...vp("state", V.state)}
                                    >
                                      <option value="">Select</option>
                                      {US_STATES.map((s) => (
                                        <option key={s.code} value={s.code}>{s.name}</option>
                                      ))}
                                    </Form.Select>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="zipCode">Zip</Form.Label>
                                    <Form.Control
                                      id="zipCode"
                                      inputMode="numeric"
                                      value={zipCode}
                                      onChange={(e) => setZipCode(e.target.value)}
                                      placeholder="#####"
                                      required
                                      {...vp("zip", V.zip)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a 5-digit zip code.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="businessType">Business Type</Form.Label>
                                    <Form.Select
                                      id="businessType"
                                      value={businessType}
                                      onChange={(e) => setBusinessType(e.target.value as BusinessType)}
                                      required
                                      {...vp("businessType", V.businessType)}
                                    >
                                      <option value="">Select type</option>
                                      <option value="sole">Sole Proprietorship</option>
                                      <option value="llc">LLC</option>
                                      <option value="s_corp">S Corp</option>
                                      <option value="c_corp">C Corp</option>
                                      <option value="partnership">Partnership</option>
                                      <option value="other">Other</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={3}>
                                    <Form.Label htmlFor="yearsInBusiness">Years in Business</Form.Label>
                                    <Form.Control
                                      id="yearsInBusiness"
                                      inputMode="numeric"
                                      value={yearsInBusiness}
                                      onChange={(e) => setYearsInBusiness(Number(e.target.value))}
                                      placeholder="0"
                                      required
                                      {...vp("yearsInBusiness", V.yearsInBusiness)}
                                    />
                                    <Form.Control.Feedback type="invalid">0–80 allowed.</Form.Control.Feedback>
                                  </Col>

                                 
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 2: OPERATIONS & COVERAGE */}
                          {currentStep === 1 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Operations &amp; Coverage</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={4}>
                                    <Form.Label htmlFor="workType">Primary Work</Form.Label>
                                    <Form.Select
                                      id="workType"
                                      value={workType}
                                      onChange={(e) => setWorkType(e.target.value as WorkType)}
                                      required
                                      {...vp("workType", V.workType)}
                                    >
                                      <option value="">Select</option>
                                      <option value="residential">Residential</option>
                                      <option value="commercial">Commercial</option>
                                      <option value="both">Both</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={8} className="pt-md-4">
                                    <div className="d-flex flex-wrap gap-3">
                                      <Form.Check id="srvRepair" type="checkbox" label="Service/Repair" checked={doesServiceRepair} onChange={(e) => setDoesServiceRepair(e.currentTarget.checked)} />
                                      <Form.Check id="newConst" type="checkbox" label="New Construction" checked={doesNewConstruction} onChange={(e) => setDoesNewConstruction(e.currentTarget.checked)} />
                                      <Form.Check id="sewerDrain" type="checkbox" label="Sewer/Drain" checked={doesSewerDrain} onChange={(e) => setDoesSewerDrain(e.currentTarget.checked)} />
                                      <Form.Check id="gasLines" type="checkbox" label="Gas Lines" checked={doesGasLines} onChange={(e) => setDoesGasLines(e.currentTarget.checked)} />
                                      <Form.Check id="waterHeaters" type="checkbox" label="Water Heaters" checked={doesWaterHeaters} onChange={(e) => setDoesWaterHeaters(e.currentTarget.checked)} />
                                      <Form.Check id="excavation" type="checkbox" label="Excavation/Trenching" checked={doesExcavation} onChange={(e) => setDoesExcavation(e.currentTarget.checked)} />
                                    </div>
                                  </Col>

                                  <Col md={3}>
                                    <Form.Label htmlFor="ownersCount">Owners</Form.Label>
                                    <Form.Control
                                      id="ownersCount"
                                      inputMode="numeric"
                                      value={ownersCount}
                                      onChange={(e) => setOwnersCount(Number(e.target.value))}
                                      placeholder="1"
                                      required
                                      {...vp("ownersCount", V.ownersCount)}
                                    />
                                    <Form.Control.Feedback type="invalid">1–10 allowed.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={3}>
                                    <Form.Label htmlFor="employeesCount">Employees</Form.Label>
                                    <Form.Control
                                      id="employeesCount"
                                      inputMode="numeric"
                                      value={employeesCount}
                                      onChange={(e) => setEmployeesCount(Number(e.target.value))}
                                      placeholder="0"
                                      required
                                      {...vp("employeesCount", V.employeesCount)}
                                    />
                                    <Form.Control.Feedback type="invalid">0–200 allowed.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={3} className="pt-md-4">
                                    <Form.Check
                                      id="usesSubs"
                                      type="checkbox"
                                      label="Use Subcontractors"
                                      checked={usesSubcontractors}
                                      onChange={(e) => setUsesSubcontractors(e.currentTarget.checked)}
                                    />
                                  </Col>

                                  <Col md={3}>
                                    <Form.Label htmlFor="subPercent">% Subcontracted</Form.Label>
                                    <InputGroup hasValidation>
                                      <Form.Control
                                        id="subPercent"
                                        inputMode="numeric"
                                        value={subcontractorPercent}
                                        onChange={(e) => setSubcontractorPercent(e.target.value)}
                                        placeholder={usesSubcontractors ? "e.g., 20" : "0"}
                                        required
                                        {...vp("subcontractorPercent", V.subcontractorPercent)}
                                      />
                                      <InputGroup.Text>%</InputGroup.Text>
                                      <Form.Control.Feedback type="invalid">0–100% when using subs.</Form.Control.Feedback>
                                    </InputGroup>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="glLimit">GL Limit</Form.Label>
                                    <Form.Select
                                      id="glLimit"
                                      value={glLimit}
                                      onChange={(e) => setGlLimit(e.target.value as GLLimit)}
                                      required
                                      {...vp("glLimit", V.glLimit)}
                                    >
                                      <option value="">Select</option>
                                      <option value="1M/2M">$1M / $2M</option>
                                      <option value="2M/4M">$2M / $4M</option>
                                      <option value="custom">Custom</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="glCustom">Custom GL (if selected)</Form.Label>
                                    <InputGroup hasValidation>
                                      <InputGroup.Text>$</InputGroup.Text>
                                      <Form.Control
                                        id="glCustom"
                                        inputMode="numeric"
                                        value={glLimitCustom}
                                        onChange={(e) => setGlLimitCustom(e.target.value)}
                                        placeholder={glLimit === "custom" ? "e.g., 3000000" : "—"}
                                        disabled={glLimit !== "custom"}
                                        {...vp("glLimitCustom", V.glLimitCustom)}
                                      />
                                    </InputGroup>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="toolsCoverage">Tools &amp; Equipment</Form.Label>
                                    <InputGroup hasValidation>
                                      <InputGroup.Text>$</InputGroup.Text>
                                      <Form.Control
                                        id="toolsCoverage"
                                        inputMode="numeric"
                                        value={toolsEquipmentCoverage}
                                        onChange={(e) => setToolsEquipmentCoverage(e.target.value)}
                                        placeholder="e.g., 10000"
                                        required
                                        {...vp("toolsEquipmentCoverage", V.toolsEquipmentCoverage)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter amount ≥ 0.</Form.Control.Feedback>
                                    </InputGroup>
                                    <div className="form-text">Hand tools, jetters, larger equipment.</div>
                                  </Col>

                                  <Col md={4} className="pt-md-4">
                                    <Form.Check
                                      id="wc"
                                      type="checkbox"
                                      label="Workers’ Comp"
                                      checked={wantsWorkersComp}
                                      onChange={(e) => setWantsWorkersComp(e.currentTarget.checked)}
                                    />
                                  </Col>

                                  <Col md={4} className="pt-md-4">
                                    <Form.Check
                                      id="auto"
                                      type="checkbox"
                                      label="Commercial Auto"
                                      checked={wantsCommercialAuto}
                                      onChange={(e) => setWantsCommercialAuto(e.currentTarget.checked)}
                                    />
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="numVehicles"># Commercial Vehicles</Form.Label>
                                    <Form.Control
                                      id="numVehicles"
                                      inputMode="numeric"
                                      value={numCommercialVehicles}
                                      onChange={(e) => setNumCommercialVehicles(Number(e.target.value))}
                                      placeholder={wantsCommercialAuto ? "e.g., 2" : "0"}
                                      required
                                      {...vp("numCommercialVehicles", V.numCommercialVehicles)}
                                    />
                                    <Form.Control.Feedback type="invalid">0–50 allowed.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4} className="pt-md-4">
                                    <Form.Check
                                      id="bonding"
                                      type="checkbox"
                                      label="Bonding Needed"
                                      checked={wantsBonding}
                                      onChange={(e) => setWantsBonding(e.currentTarget.checked)}
                                    />
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 3: RISK & NOTES */}
                          {currentStep === 2 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Risk &amp; Notes</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={3}>
                                    <Form.Label htmlFor="claims">Prior Claims (5y)</Form.Label>
                                    <Form.Control
                                      id="claims"
                                      inputMode="numeric"
                                      value={priorClaimsCount5y}
                                      onChange={(e) => setPriorClaimsCount5y(Number(e.target.value))}
                                      placeholder="0"
                                      required
                                      {...vp("priorClaimsCount5y", V.priorClaimsCount5y)}
                                    />
                                    <Form.Control.Feedback type="invalid">0–20 allowed.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={3}>
                                    <Form.Label htmlFor="licenseNumber">License # (optional)</Form.Label>
                                    <Form.Control
                                      id="licenseNumber"
                                      value={licenseNumber}
                                      onChange={(e) => setLicenseNumber(e.target.value)}
                                      placeholder="State/city license"
                                      {...vpOpt("licenseNumber", !!licenseNumber, V.licenseNumber)}
                                    />
                                  </Col>

                                  <Col md={3}>
                                    <Form.Label htmlFor="annualRevenue">Annual Revenue (optional)</Form.Label>
                                    <InputGroup hasValidation>
                                      <InputGroup.Text>$</InputGroup.Text>
                                      <Form.Control
                                        id="annualRevenue"
                                        inputMode="numeric"
                                        value={annualRevenue}
                                        onChange={(e) => setAnnualRevenue(e.target.value)}
                                        placeholder="e.g., 450000"
                                        {...vpOpt("annualRevenue", !!annualRevenue, V.annualRevenue)}
                                      />
                                    </InputGroup>
                                  </Col>

                                  <Col md={3}>
                                    <Form.Label htmlFor="annualPayroll">Annual Payroll (optional)</Form.Label>
                                    <InputGroup hasValidation>
                                      <InputGroup.Text>$</InputGroup.Text>
                                      <Form.Control
                                        id="annualPayroll"
                                        inputMode="numeric"
                                        value={annualPayroll}
                                        onChange={(e) => setAnnualPayroll(e.target.value)}
                                        placeholder="e.g., 220000"
                                        {...vpOpt("annualPayroll", !!annualPayroll, V.annualPayroll)}
                                      />
                                    </InputGroup>
                                  </Col>

                                  <Col md={4} className="pt-md-4">
                                    <Form.Check
                                      id="torch"
                                      type="checkbox"
                                      label="Torch/Soldering Performed"
                                      checked={doesTorchSoldering}
                                      onChange={(e) => setDoesTorchSoldering(e.currentTarget.checked)}
                                    />
                                  </Col>

                                  <Col md={12}>
                                    <Form.Label htmlFor="notes">Notes (optional)</Form.Label>
                                    <Form.Control
                                      id="notes"
                                      as="textarea"
                                      rows={3}
                                      value={notes}
                                      onChange={(e) => setNotes(e.target.value)}
                                      placeholder="Project requirements, AI/CG 20 10, waiver of subrogation, per-project aggregate, etc."
                                      {...vp("notes", V.notes)}
                                    />
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 4: CONFIRMATION */}
                          {currentStep === 3 && (
                            <Col xs={12}>
                              <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls (no buttons on final step) */}
                        {currentStep < 3 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                goBack();
                              }}
                              disabled={currentStep === 0}
                            >
                              Previous
                            </Button>

                            {currentStep < 2 ? (
                              <Button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isStepValid(currentStep)) goNext();
                                }}
                                disabled={!isStepValid(currentStep)}
                              >
                                Next
                              </Button>
                            ) : (
                              <Button type="submit" variant="primary" size="lg" disabled={submitting || !valid}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      /* ---------- VERTICAL (all-in-one) ---------- */
                      <>
                        {!submitOk ? (
                          <>
                            <Row className="g-3">
                              {/* Contact & Business */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Contact &amp; Business</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={6}>
                                      <Form.Label htmlFor="fullNameV">Full Name</Form.Label>
                                      <Form.Control id="fullNameV" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe" required {...vp("fullName", V.fullName)} />
                                      <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Label htmlFor="companyNameV">Company Name</Form.Label>
                                      <Form.Control id="companyNameV" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Acme Plumbing LLC" required {...vp("companyName", V.companyName)} />
                                      <Form.Control.Feedback type="invalid">Please enter your company name.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Label htmlFor="emailV">Email</Form.Label>
                                      <Form.Control id="emailV" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required {...vp("email", V.email)} />
                                      <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Label htmlFor="phoneV">Phone</Form.Label>
                                      <Form.Control id="phoneV" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" required {...vp("phone", V.phone)} />
                                      <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={8}>
                                      <Form.Label htmlFor="address1V">Business Address</Form.Label>
                                      <Form.Control id="address1V" value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="123 Main St" required {...vp("address1", V.address1)} />
                                      <Form.Control.Feedback type="invalid">Enter your street address.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={4}>
                                      <Form.Label htmlFor="address2V">Unit / Suite</Form.Label>
                                      <Form.Control id="address2V" value={address2} onChange={(e) => setAddress2(e.target.value)} placeholder="Suite 200" {...vpOpt("address2", !!address2.trim())} />
                                    </Col>
                                    <Col md={4}>
                                      <Form.Label htmlFor="cityV">City</Form.Label>
                                      <Form.Control id="cityV" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" required {...vp("city", V.city)} />
                                      <Form.Control.Feedback type="invalid">Enter your city.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={4}>
                                      <Form.Label htmlFor="stateV">State</Form.Label>
                                      <Form.Select id="stateV" value={state} onChange={(e) => setState(e.target.value)} required {...vp("state", V.state)}>
                                        <option value="">Select</option>
                                        {US_STATES.map((s) => (<option key={s.code} value={s.code}>{s.name}</option>))}
                                      </Form.Select>
                                    </Col>
                                    <Col md={4}>
                                      <Form.Label htmlFor="zipCodeV">Zip</Form.Label>
                                      <Form.Control id="zipCodeV" inputMode="numeric" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="#####" required {...vp("zip", V.zip)} />
                                      <Form.Control.Feedback type="invalid">Enter a 5-digit zip code.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Label htmlFor="businessTypeV">Business Type</Form.Label>
                                      <Form.Select id="businessTypeV" value={businessType} onChange={(e) => setBusinessType(e.target.value as BusinessType)} required {...vp("businessType", V.businessType)}>
                                        <option value="">Select type</option>
                                        <option value="sole">Sole Proprietorship</option>
                                        <option value="llc">LLC</option>
                                        <option value="s_corp">S Corp</option>
                                        <option value="c_corp">C Corp</option>
                                        <option value="partnership">Partnership</option>
                                        <option value="other">Other</option>
                                      </Form.Select>
                                    </Col>
                                    <Col md={3}>
                                      <Form.Label htmlFor="yearsInBusinessV">Years in Business</Form.Label>
                                      <Form.Control id="yearsInBusinessV" inputMode="numeric" value={yearsInBusiness} onChange={(e) => setYearsInBusiness(Number(e.target.value))} placeholder="0" required {...vp("yearsInBusiness", V.yearsInBusiness)} />
                                      <Form.Control.Feedback type="invalid">0–80 allowed.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={3}>
                                      <Form.Label htmlFor="websiteV">Website (optional)</Form.Label>
                                      <Form.Control id="websiteV" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://..." {...vpOpt("website", !!website, V.website)} />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* Operations & Coverage */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Operations &amp; Coverage</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={4}>
                                      <Form.Label htmlFor="workTypeV">Primary Work</Form.Label>
                                      <Form.Select id="workTypeV" value={workType} onChange={(e) => setWorkType(e.target.value as WorkType)} required {...vp("workType", V.workType)}>
                                        <option value="">Select</option>
                                        <option value="residential">Residential</option>
                                        <option value="commercial">Commercial</option>
                                        <option value="both">Both</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={8} className="pt-md-4">
                                      <div className="d-flex flex-wrap gap-3">
                                        <Form.Check id="srvRepairV" type="checkbox" label="Service/Repair" checked={doesServiceRepair} onChange={(e) => setDoesServiceRepair(e.currentTarget.checked)} />
                                        <Form.Check id="newConstV" type="checkbox" label="New Construction" checked={doesNewConstruction} onChange={(e) => setDoesNewConstruction(e.currentTarget.checked)} />
                                        <Form.Check id="sewerDrainV" type="checkbox" label="Sewer/Drain" checked={doesSewerDrain} onChange={(e) => setDoesSewerDrain(e.currentTarget.checked)} />
                                        <Form.Check id="gasLinesV" type="checkbox" label="Gas Lines" checked={doesGasLines} onChange={(e) => setDoesGasLines(e.currentTarget.checked)} />
                                        <Form.Check id="waterHeatersV" type="checkbox" label="Water Heaters" checked={doesWaterHeaters} onChange={(e) => setDoesWaterHeaters(e.currentTarget.checked)} />
                                        <Form.Check id="excavationV" type="checkbox" label="Excavation/Trenching" checked={doesExcavation} onChange={(e) => setDoesExcavation(e.currentTarget.checked)} />
                                      </div>
                                    </Col>

                                    <Col md={3}>
                                      <Form.Label htmlFor="ownersCountV">Owners</Form.Label>
                                      <Form.Control id="ownersCountV" inputMode="numeric" value={ownersCount} onChange={(e) => setOwnersCount(Number(e.target.value))} placeholder="1" required {...vp("ownersCount", V.ownersCount)} />
                                      <Form.Control.Feedback type="invalid">1–10 allowed.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={3}>
                                      <Form.Label htmlFor="employeesCountV">Employees</Form.Label>
                                      <Form.Control id="employeesCountV" inputMode="numeric" value={employeesCount} onChange={(e) => setEmployeesCount(Number(e.target.value))} placeholder="0" required {...vp("employeesCount", V.employeesCount)} />
                                      <Form.Control.Feedback type="invalid">0–200 allowed.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={3} className="pt-md-4">
                                      <Form.Check id="usesSubsV" type="checkbox" label="Use Subcontractors" checked={usesSubcontractors} onChange={(e) => setUsesSubcontractors(e.currentTarget.checked)} />
                                    </Col>

                                    <Col md={3}>
                                      <Form.Label htmlFor="subPercentV">% Subcontracted</Form.Label>
                                      <InputGroup hasValidation>
                                        <Form.Control id="subPercentV" inputMode="numeric" value={subcontractorPercent} onChange={(e) => setSubcontractorPercent(e.target.value)} placeholder={usesSubcontractors ? "e.g., 20" : "0"} required {...vp("subcontractorPercent", V.subcontractorPercent)} />
                                        <InputGroup.Text>%</InputGroup.Text>
                                        <Form.Control.Feedback type="invalid">0–100% when using subs.</Form.Control.Feedback>
                                      </InputGroup>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="glLimitV">GL Limit</Form.Label>
                                      <Form.Select id="glLimitV" value={glLimit} onChange={(e) => setGlLimit(e.target.value as GLLimit)} required {...vp("glLimit", V.glLimit)}>
                                        <option value="">Select</option>
                                        <option value="1M/2M">$1M / $2M</option>
                                        <option value="2M/4M">$2M / $4M</option>
                                        <option value="custom">Custom</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="glCustomV">Custom GL (if selected)</Form.Label>
                                      <InputGroup hasValidation>
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control id="glCustomV" inputMode="numeric" value={glLimitCustom} onChange={(e) => setGlLimitCustom(e.target.value)} placeholder={glLimit === "custom" ? "e.g., 3000000" : "—"} disabled={glLimit !== "custom"} {...vp("glLimitCustom", V.glLimitCustom)} />
                                      </InputGroup>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="toolsCoverageV">Tools &amp; Equipment</Form.Label>
                                      <InputGroup hasValidation>
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control id="toolsCoverageV" inputMode="numeric" value={toolsEquipmentCoverage} onChange={(e) => setToolsEquipmentCoverage(e.target.value)} placeholder="e.g., 10000" required {...vp("toolsEquipmentCoverage", V.toolsEquipmentCoverage)} />
                                        <Form.Control.Feedback type="invalid">Enter amount ≥ 0.</Form.Control.Feedback>
                                      </InputGroup>
                                    </Col>

                                    <Col md={4} className="pt-md-4">
                                      <Form.Check id="wcV" type="checkbox" label="Workers’ Comp" checked={wantsWorkersComp} onChange={(e) => setWantsWorkersComp(e.currentTarget.checked)} />
                                    </Col>

                                    <Col md={4} className="pt-md-4">
                                      <Form.Check id="autoV" type="checkbox" label="Commercial Auto" checked={wantsCommercialAuto} onChange={(e) => setWantsCommercialAuto(e.currentTarget.checked)} />
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="numVehiclesV"># Commercial Vehicles</Form.Label>
                                      <Form.Control id="numVehiclesV" inputMode="numeric" value={numCommercialVehicles} onChange={(e) => setNumCommercialVehicles(Number(e.target.value))} placeholder={wantsCommercialAuto ? "e.g., 2" : "0"} required {...vp("numCommercialVehicles", V.numCommercialVehicles)} />
                                      <Form.Control.Feedback type="invalid">0–50 allowed.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4} className="pt-md-4">
                                      <Form.Check id="bondingV" type="checkbox" label="Bonding Needed" checked={wantsBonding} onChange={(e) => setWantsBonding(e.currentTarget.checked)} />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>
                            </Row>

                            {/* Risk & Notes */}
                            <Row className="g-3">
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Risk &amp; Notes</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={3}>
                                      <Form.Label htmlFor="claimsV">Prior Claims (5y)</Form.Label>
                                      <Form.Control id="claimsV" inputMode="numeric" value={priorClaimsCount5y} onChange={(e) => setPriorClaimsCount5y(Number(e.target.value))} placeholder="0" required {...vp("priorClaimsCount5y", V.priorClaimsCount5y)} />
                                      <Form.Control.Feedback type="invalid">0–20 allowed.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={3}>
                                      <Form.Label htmlFor="licenseNumberV">License # (optional)</Form.Label>
                                      <Form.Control id="licenseNumberV" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} placeholder="State/city license" {...vpOpt("licenseNumber", !!licenseNumber, V.licenseNumber)} />
                                    </Col>
                                    <Col md={3}>
                                      <Form.Label htmlFor="annualRevenueV">Annual Revenue (optional)</Form.Label>
                                      <InputGroup hasValidation>
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control id="annualRevenueV" inputMode="numeric" value={annualRevenue} onChange={(e) => setAnnualRevenue(e.target.value)} placeholder="e.g., 450000" {...vpOpt("annualRevenue", !!annualRevenue, V.annualRevenue)} />
                                      </InputGroup>
                                    </Col>
                                    <Col md={3}>
                                      <Form.Label htmlFor="annualPayrollV">Annual Payroll (optional)</Form.Label>
                                      <InputGroup hasValidation>
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control id="annualPayrollV" inputMode="numeric" value={annualPayroll} onChange={(e) => setAnnualPayroll(e.target.value)} placeholder="e.g., 220000" {...vpOpt("annualPayroll", !!annualPayroll, V.annualPayroll)} />
                                      </InputGroup>
                                    </Col>
                                    <Col md={4} className="pt-md-4">
                                      <Form.Check id="torchV" type="checkbox" label="Torch/Soldering Performed" checked={doesTorchSoldering} onChange={(e) => setDoesTorchSoldering(e.currentTarget.checked)} />
                                    </Col>
                                    <Col md={12}>
                                      <Form.Label htmlFor="notesV">Notes (optional)</Form.Label>
                                      <Form.Control id="notesV" as="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Project requirements, AI/CG 20 10, waiver of subrogation, per-project aggregate, etc." {...vp("notes", V.notes)} />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>
                            </Row>

                            {/* Submit */}
                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about insurance options. No spam. You can opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                        )}
                      </>
                    )}
                  </Form>
                  {/* FORM END */}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Three simple steps to protect your plumbing business.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">1</div>
                <div>
                  <h6 className="mb-1">Tell us about your business</h6>
                  <p className="mb-0 text-body-secondary">Share company info, location, and your work mix.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">2</div>
                <div>
                  <h6 className="mb-1">Choose coverage</h6>
                  <p className="mb-0 text-body-secondary">GL limits, Workers’ Comp, Auto, Tools, and bonds.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">3</div>
                <div>
                  <h6 className="mb-1">We compare options</h6>
                  <p className="mb-0 text-body-secondary">We shop carriers, and help you choose the best insurance to protect your business.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-5 bg-light border-top">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">Frequently Asked Questions</h2>
              <p className="text-body-secondary mb-0">Quick answers about plumbers insurance.</p>
            </Col>
          </Row>
          <FAQ_v01_Simple faqs={plumberFAQs} openFirst />
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Discount Plumbers Insurance. All rights reserved.
              <div className="mt-2">
                Insurance services subject to licensing and state availability. This page is for general information and not legal advice.
              </div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033374433">Call {AGENT_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

/* ------------------------------------------------------------
   Confirmation block (Sectioned, readable, no JSON)
------------------------------------------------------------ */
const ConfirmationBlock: React.FC<{
  status: { ok: boolean; status?: number; id?: string; message?: string } | null;
  app: ILeadPayload | null;
}> = ({ status, app }) => {
  const ok = !!status?.ok;

  if (!app) {
    return (
      <Card className="border-0">
        <Card.Body className="p-4">
          <Alert variant="warning" className="mb-0">We don’t have your details to display yet.</Alert>
        </Card.Body>
      </Card>
    );
    }

  const services: string[] = [
    app.doesServiceRepair ? "Service/Repair" : null,
    app.doesNewConstruction ? "New Construction" : null,
    app.doesSewerDrain ? "Sewer/Drain" : null,
    app.doesGasLines ? "Gas Lines" : null,
    app.doesWaterHeaters ? "Water Heaters" : null,
    app.doesExcavation ? "Excavation/Trenching" : null,
  ].filter(Boolean) as string[];

  return (
    <Card className="border-0">
      <Card.Body className="p-4">
        {/* Status */}
        {ok ? (
          <Alert variant="success" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">Thank you! A licensed representative will contact you shortly.</div>
              <div className="small">
                Submission status: <strong>{status?.status ?? 200}</strong>
                {status?.id ? <> • Reference ID: <strong>{status.id}</strong></> : null}
              </div>
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">We couldn’t confirm your submission.</div>
              <div className="small">{status?.message || "Please try again or give us a call."}</div>
            </div>
          </Alert>
        )}

        {/* Sections */}
        <div className="mt-3">
          {/* Contact */}
          <Section title="Contact">
            <KV label="Name"  value={dash(app.fullName)} />
            <KV label="Email" value={dash(app.email)} />
            <KV label="Phone" value={formatPhonePretty(app.phone)} />
          </Section>

          {/* Business */}
          <Section title="Business">
            <KV label="Company"         value={dash(app.companyName)} />
            <KV label="Business Type"   value={dash(app.businessType.replace("_", "-").toUpperCase())} />
            <KV label="Years in Biz"    value={String(app.yearsInBusiness)} />
            <KV label="Website"         value={dash(app.website)} />
          </Section>

          {/* Location */}
          <Section title="Location">
            <KV label="Street"            value={dash(app.address1)} />
            <KV label="Unit / Suite"      value={dash(app.address2)} />
            <KV label="City / State / Zip" value={`${dash(app.city)}, ${dash(app.state)} ${dash(app.zipCode)}`} />
          </Section>

          {/* Operations */}
          <Section title="Operations">
            <KV label="Work Type"         value={dash(cap(app.workType))} />
            <KV label="Services"          value={services.length ? services.join(", ") : "—"} />
            <KV label="Owners / Employees" value={`${app.ownersCount} / ${app.employeesCount}`} />
            <KV label="Subcontractors"    value={app.usesSubcontractors ? `${app.subcontractorPercent}%` : "No"} />
          </Section>

          {/* Coverage */}
          <Section title="Coverage">
            <KV label="GL Limit"          value={app.glLimit === "custom" ? money(app.glLimitCustom || "") : app.glLimit} />
            <KV label="Workers’ Comp"     value={app.wantsWorkersComp ? "Yes" : "No"} />
            <KV label="Commercial Auto"   value={app.wantsCommercialAuto ? `Yes (${app.numCommercialVehicles} vehicles)` : "No"} />
            <KV label="Bonding"           value={app.wantsBonding ? "Yes" : "No"} />
            <KV label="Tools & Equipment" value={money(app.toolsEquipmentCoverage)} />
          </Section>

            {/* Risk & Notes */}
          <Section title="Risk & Notes">
            <KV label="Prior Claims (5y)" value={String(app.priorClaimsCount5y)} />
            <KV label="License #"         value={dash(app.licenseNumber)} />
            <KV label="Annual Revenue"    value={dash(app.annualRevenue ? money(app.annualRevenue) : "")} />
            <KV label="Annual Payroll"    value={dash(app.annualPayroll ? money(app.annualPayroll) : "")} />
            <KV label="Torch/Soldering"   value={app.doesTorchSoldering ? "Yes" : "No"} />
          </Section>

          {/* Notes */}
          <Section title="Notes">
            <div className="p-3 bg-body-tertiary border rounded">{dash(app.notes)}</div>
          </Section>
        </div>

        {/* Actions */}
        <div className="mt-4 d-flex flex-wrap gap-2">
          <Button href="tel:3033374433" variant="primary">Call {AGENT_PHONE_NUMBER}</Button>
          <Button variant="outline-secondary" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Back to top</Button>
        </div>
      </Card.Body>
    </Card>
  );
};

/* ---------- Tiny helpers for layout ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="text-uppercase text-muted mb-2">{title}</h6>
    <Row as="dl" className="row-cols-1 row-cols-md-2 g-3 mb-0">
      {children}
    </Row>
    <hr className="mt-3 mb-0" />
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Col className="mb-0">
    <dt className="small text-muted mb-1">{label}</dt>
    <dd className="mb-0">{value}</dd>
  </Col>
);

export default DISCOUNT_PLUMBERS_LandingPage_v02O_WIZARD;
